<script lang="ts" module>
  import type { BaseProps } from "#lib/internal/types"

  export type TooltipTriggerProps = BaseProps & {
    as?: "div" | "button" | "a"
    ref?: HTMLElement | null
    type?: BaseProps<"button">["type"]
    disabled?: boolean
  }
</script>

<script lang="ts">
  import { Popper } from "#lib/components/ui/popper/index"
  import { onDestroy, type Snippet } from "svelte"
  import { getTooltipProviderContext } from "./TooltipProvider.svelte"
  import { getTooltipRootContext } from "./TooltipRoot.svelte"

  type Props = TooltipTriggerProps & {
    children?: Snippet
    onclick?: (event: MouseEvent) => void
  }

  let { use = [], children, onclick, ...restProps }: Props = $props()

  const ctx = getTooltipRootContext()
  const providerCtx = getTooltipProviderContext()

  let isPointerDown = false
  let hasPointerMoveOpened = false
  const handlePointerUp = () => {
    isPointerDown = false
  }

  onDestroy(() => {
    document.removeEventListener("pointerup", handlePointerUp)
  })
</script>

<Popper.Anchor
  as="button"
  aria-describedby={ctx.open ? ctx.contentId : undefined}
  data-state={ctx.stateAttribute}
  bind:ref={ctx.trigger}
  {...restProps}
  onpointermove={(event) => {
    if (event.pointerType === "touch") return
    if (!hasPointerMoveOpened && !providerCtx?.isPointerInTransit) {
      ctx.onTriggerEnter()
      hasPointerMoveOpened = true
    }
  }}
  onpointerleave={() => {
    ctx.onTriggerLeave()
    hasPointerMoveOpened = false
  }}
  onpointerdown={() => {
    isPointerDown = true
    document.addEventListener("pointerup", handlePointerUp, { once: true })
  }}
  onfocus={() => {
    if (isPointerDown) return
    ctx.onOpen()
  }}
  onblur={() => {
    ctx.onClose()
  }}
  onclick={(event) => {
    ctx.onClose()
    // Optional chaining only guards null/undefined — a truthy non-function
    // (e.g. a Symbol injected by third-party click instrumentation)
    // still throws "is not a function". Guard by type. See WEB-BASED-2-9ZR.
    if (typeof onclick === "function") onclick(event)
  }}
  use={[...(use ?? [])]}
>
  {@render children?.()}
</Popper.Anchor>
